import { createFeatureSelector, createSelector } from '@ngrx/store';
import { Account } from '../../../models/account.model';
import { PaymentOrderCreate } from '../../../models/payment-order-create.model';
import { PurposeCode } from '../../../models/purpose-code.model';
import * as fromAccounts from '../../reducers/accounts/accounts.reducer';
import * as fromDashboard from '../../reducers/dashboard/dashboard.reducer';
import * as fromTemplates from '../../reducers/templates/templates.reducer';
import { PaymentInternalCreate } from './../../../models/payment-internal.model';
import { Saving } from './../../../models/saving.model';

export const selectHomeStateFeature =
  createFeatureSelector<fromDashboard.State>(fromDashboard.dashboardFeatureKey);

export const selectAccountStateFeature =
  createFeatureSelector<fromAccounts.State>(fromAccounts.accountFeatureKey);

export const selectTemplatesStateFeature =
  createFeatureSelector<fromTemplates.State>(fromTemplates.templatesFeatureKey);

// INTERFACES
export interface TemplatesViewModel {
  accounts: Account[] | null;
  savings: Saving[] | null;
  selectedAccount: Account | null;
  selectedSaving: Saving | null;
}

export interface PaymentTemplateModel {
  templates: PaymentOrderCreate[] | null;
}

export interface TemplateSelectedIdModel {
  selectedTemplates: PaymentOrderCreate[] | null;
}

export interface TemplateDetailsModel {
  templateDetails: PaymentOrderCreate | null;
}

export interface PopulateTemplateDetailsFormModel {
  templateDetailsForm: PaymentOrderCreate | null;
  selectedAccount: Account | null;
}

export interface ConfirmPaymentModel {
  selectedAccount: Account | null;
  paymentOrderDetailsForm: PaymentOrderCreate | null;
  internalOrderDetailsForm: PaymentInternalCreate | null;
}

export interface TemplatePurposeCodeModel {
  purposeCodes: PurposeCode[] | undefined;
}

export interface TemplatePoollingTimeoutSecondsModel {
  pollingTimeoutSeconds: number | null;
}

// SELECTORS
export const selectTemplatesViewModel = createSelector(
  selectHomeStateFeature,
  selectAccountStateFeature,
  (
    dashboardState: fromDashboard.State,
    accountsState: fromAccounts.State
  ): TemplatesViewModel => {
    return {
      accounts: dashboardState.dashboard
        ? dashboardState.dashboard.accounts
        : null,
      savings: dashboardState.dashboard
        ? dashboardState.dashboard.savings
        : null,
      selectedAccount: accountsState.selectedAccount,
      selectedSaving: accountsState.selectedSaving
    };
  }
);

export const selectTemplatesTemplate = createSelector(
  selectTemplatesStateFeature,
  (state: fromTemplates.State): PaymentTemplateModel => {
    return {
      templates: state.templates
    };
  }
);

export const selectedTemplatesByIds = createSelector(
  selectTemplatesStateFeature,
  (state: fromTemplates.State): TemplateSelectedIdModel => {
    return {
      selectedTemplates: state.selectedTemplate ? state.selectedTemplate : null
    };
  }
);

export const selectTemplateDetails = createSelector(
  selectTemplatesStateFeature,
  (state: fromTemplates.State): TemplateDetailsModel => {
    return {
      templateDetails: state.templateDetails
    };
  }
);

export const selectTemplatePurposeCode = createSelector(
  selectHomeStateFeature,
  (state: fromDashboard.State): TemplatePurposeCodeModel => {
    return {
      purposeCodes: state.dashboard?.purposeCodes
    };
  }
);

export const selectPopulateTemplateDetailsForm = createSelector(
  selectAccountStateFeature,
  selectTemplatesStateFeature,
  (
    accState: fromAccounts.State,
    state: fromTemplates.State
  ): PopulateTemplateDetailsFormModel => {
    return {
      templateDetailsForm: state.templateDetailsForm,
      selectedAccount: accState.selectedAccount
    };
  }
);

export const selectConfirmPaymentModel = createSelector(
  selectAccountStateFeature,
  selectTemplatesStateFeature,
  (
    accState: fromAccounts.State,
    templatesState: fromTemplates.State
  ): ConfirmPaymentModel => {
    return {
      selectedAccount: accState.selectedAccount,
      paymentOrderDetailsForm: templatesState.paymentOrderDetailsForm,
      internalOrderDetailsForm: templatesState.internalOrderDetailsForm
    };
  }
);

export const selectPollingTimeoutSeconds = createSelector(
  selectHomeStateFeature,
  (state: fromDashboard.State): TemplatePoollingTimeoutSecondsModel => {
    return {
      pollingTimeoutSeconds: state.dashboard!.pollingTimeoutSeconds
    };
  }
);